/**
 * useCallRtpStats - What the media path is actually carrying, right now
 *
 * Reads `VoipMonitorService`, which already polls `getStats()` every two
 * seconds for the connection-quality indicator. A second poller on the same
 * peer connection would be pure duplication — and worse, it would compute
 * packet loss its own way, so the panel and the indicator beside it could
 * disagree about the same call.
 *
 * The figures are per-interval, not lifetime totals: a call that lost 4% in its
 * first ten seconds and has been clean since is a very different call from one
 * losing 4% now, and a cumulative average cannot tell them apart. The export
 * keeps the totals; this card is read while something is going wrong.
 */

import type { CallRtpStats } from 'Modules/softphone/components/SoftphoneDevTools/hooks/useCallRtpStats/index.types';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useEffect, useState } from 'react';
import VoipMonitorService from 'Services/voipMonitorService';
import type { CollectedStats } from 'Services/voipMonitorService/index.types';
import { calculatePacketLossRate } from 'Services/voipMonitorService/utils';

/**
 * Half the service's own 2s collection interval, so a new sample is picked up
 * within one tick of being taken. This reads an array the service already
 * filled; it does not call `getStats()`.
 */
export const CALL_STATS_POLL_INTERVAL_MS = 1000;

const EMPTY: CallRtpStats = {
  isMonitored: false,
  iceConnectionState: null,
  sendKbps: null,
  receiveKbps: null,
  lossPercent: null,
  jitterMs: null,
  roundTripMs: null,
};

/** Bits per second between two cumulative byte counters, as kbps. */
const toKbps = (
  current: number | undefined,
  previous: number | undefined,
  elapsedMs: number
): number | null => {
  if (current === undefined || previous === undefined || elapsedMs <= 0) {
    return null;
  }

  const bytes = current - previous;

  return bytes < 0 ? null : Math.round((bytes * 8) / elapsedMs);
};

const readStats = (callId: string): CallRtpStats => {
  // Monitoring follows the current call only, so metrics from another call
  // must never be shown against this one.
  if (VoipMonitorService.getMonitoredCallId() !== callId) {
    return EMPTY;
  }

  const history: CollectedStats[] = VoipMonitorService.getMetricsHistory();
  const latest = history[history.length - 1];
  const previous = history[history.length - 2];
  const iceConnectionState =
    WebRTCService.getInstance().getPeerConnection(callId)
      ?.iceConnectionState ?? null;

  if (!latest) {
    return { ...EMPTY, isMonitored: true, iceConnectionState };
  }

  const elapsedMs = previous
    ? new Date(latest.time).getTime() - new Date(previous.time).getTime()
    : 0;

  return {
    isMonitored: true,
    iceConnectionState,
    sendKbps: toKbps(
      latest.outboundAudio?.bytesSent,
      previous?.outboundAudio?.bytesSent,
      elapsedMs
    ),
    receiveKbps: toKbps(
      latest.inboundAudio?.bytesReceived,
      previous?.inboundAudio?.bytesReceived,
      elapsedMs
    ),
    // The service's own loss maths, so the panel and the connection-quality
    // indicator can never disagree about the same call.
    lossPercent: previous ? calculatePacketLossRate(history.slice(-2)) : null,
    // `jitter` comes off the RTP report in seconds, already smoothed per RFC 3550.
    jitterMs:
      latest.basicStats?.jitter === undefined
        ? null
        : Math.round(latest.basicStats.jitter * 1000),
    roundTripMs:
      latest.basicStats?.roundTripTime === undefined
        ? null
        : Math.round(latest.basicStats.roundTripTime * 1000),
  };
};

export const useCallRtpStats = (
  callId: string,
  isActive = true
): CallRtpStats => {
  const [stats, setStats] = useState<CallRtpStats>(EMPTY);

  useEffect(() => {
    if (!isActive) {
      setStats(EMPTY);

      return;
    }

    const read = () => setStats(readStats(callId));

    read();
    const interval = setInterval(read, CALL_STATS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [callId, isActive]);

  return stats;
};
